import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { AutocompleteCollege } from "./AutocompleteCollege";
import { useTheme } from "../hooks/ThemeContext";
import "../styles/RegisterPage.css";
import * as Sentry from '@sentry/react';

// Shows a message with one phrase in it turned into a link (e.g. "your school's Rezify sign-up page").
const renderMessageWithLink = (text, link) => {
    const message = String(text);
    if (!link || !link.url || !link.text || !message.includes(link.text)) return message;
    const [before, ...after] = message.split(link.text);
    return <>{before}<a href={link.url}>{link.text}</a>{after.join(link.text)}</>;
};

function RegisterPanel({
    lockedEmail = "",
    authType = "password",
    prefillFirstName = "",
    prefillLastName = ""
}) {
    const [firstWait, setFirstWait] = useState(false);
    const [waiting, setWaiting] = useState(false);
    const [error_message, setErrorMessage] = useState("");
    const [error_link, setErrorLink] = useState(null);

    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [reportedCollege, setReportedCollege] = useState("");
    const subdomain = window.location.hostname.split(".")[0];
    const [validColleges, setValidColleges] = useState([]);
    const { theme } = useTheme();
    const [adminReg, setAdminReg] = useState(null);

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const location = useLocation();

    const googleManaged = authType === "google";
    const microsoftManaged = authType === "microsoft";
    const providerManaged = googleManaged || microsoftManaged;

    useEffect(() => {
        if (lockedEmail) {
            setEmail(lockedEmail);
        }
    }, [lockedEmail]);

    useEffect(() => {
        if (prefillFirstName && !firstName) {
            setFirstName(prefillFirstName);
        }
        if (prefillLastName && !lastName) {
            setLastName(prefillLastName);
        }
    }, [prefillFirstName, prefillLastName]);


    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        setAdminReg(params.get("type") === "admin");
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);

        if (params.get("oauthError") === "true") {
            setErrorMessage("Authentication failed. Please try again.");
        }

        if (params.get("googleNotFound") === "true") {
            setErrorMessage("No account associated with this email. Please register to continue.");
        }

        if (params.get("microsoftNotFound") === "true") {
            setErrorMessage("No account associated with this email. Please register to continue.");
        }
    }, []);

    const registerWithCode = useMemo(() => {
        if (adminReg) return false;
        const params = new URLSearchParams(location.search);
        return params.get("withCode") === "True";
    }, [location.search, adminReg]);

    const validateRegisterForm = (event) => {
        const inputs = document.querySelectorAll('input[type="text"], input[type="email"]');
        const forbiddenKeywords = ["DROP", "DELETE", "INSERT", "UPDATE", "SELECT", "--", ";"];

        for (let input of inputs) {
            const value = input.value.trim();
            const upperValue = value.toUpperCase();

            for (let keyword of forbiddenKeywords) {
                if (upperValue.includes(keyword)) {
                    alert(`Input rejected in "${input.placeholder}". Please try again.`);
                    input.focus();
                    event.preventDefault();
                    return false;
                }
            }
        }

        if (
            !validColleges.includes(reportedCollege) &&
            reportedCollege !== "None" &&
            reportedCollege !== "School not listed"
        ) {
            alert("Please select a valid college from the suggestions.");
            event.preventDefault();
            return false;
        }

        return true;
    };

    const HandleRegister = async (event) => {
        try {
            event.preventDefault();
            setWaiting(true);
            setErrorLink(null);

            const result = await fetch("/api/register", {
                method: "POST",
                body: new FormData(event.target),
            });

            if (result.ok) {
                result.json().then((data) => {
                    if (data.redirect) {
                        setWaiting(false);
                        window.location.href = data.redirect;
                    } else if (data.error_message) {
                        setErrorMessage(data.error_message);
                        setErrorLink(data.error_link || null);
                        setWaiting(false);
                    } else {
                        setWaiting(false);
                    }
                });
            } else {
                const data = await result.json();
                setErrorMessage(data.error_message || "Registration failed. Please try again.");
                setWaiting(false);
            }
        } catch (e) {
            Sentry.captureException(e);
            setErrorMessage('Network error. Please try again.');
            setWaiting(false);
        }
    };

    const HandleAdminRegister = async (event) => {
        event.preventDefault();
        setWaiting(true);

        try {
            const result = await fetch("/api/admin_register", {
                method: "POST",
                body: new FormData(event.target),
            });

            const data = await result.json();

            if (!result.ok) {
                setErrorMessage(data.error_message || 'An error occurred. Please try again.');
                return;
            }

            if (data.redirect) {
                window.location.href = data.redirect;
                return;
            }

            if (data.error_message) {
                setErrorMessage(data.error_message);
            }
        } catch (e) {
            setErrorMessage('Network error. Please try again.');
            Sentry.captureException(e);
        } finally {
            setWaiting(false);
        }
    };

    useEffect(() => {
        if (adminReg !== null) {
            const endpoint = adminReg ? "/api/admin_register" : "/api/register";

            (async () => {
                try {
                    const response = await fetch(`${endpoint}${window.location.search}`);
                    const data = await response.json();

                    if (!response.ok) {
                        setErrorMessage(data.error_message || 'Failed to load. Please try again.');
                        return;
                    }

                    if (data.resume_info) {
                        if (data.resume_info.first_name) {
                            setFirstName(data.resume_info.first_name);
                        }
                        if (data.resume_info.last_name) {
                            setLastName(data.resume_info.last_name);
                        }
                        if (data.resume_info.email) {
                            setEmail(data.resume_info.email);
                        }
                        if (data.resume_info.reported_college) {
                            setReportedCollege(data.resume_info.reported_college);
                        }
                    }

                    if (data.error_message) {
                        setErrorMessage(data.error_message);
                    }
                } catch (e) {
                    setErrorMessage('Network error. Please try again.');
                    Sentry.captureException(e);
                } finally {
                    setFirstWait(true);
                }
            })();
        }
    }, [adminReg]);

    const passwordChecks = useMemo(() => {
        const min = password.length >= 8;
        const upper = /[A-Z]/.test(password);
        const special = /[!@#$%^&*(),.?":{}|<>]/.test(password);
        const match = password.length > 0 && confirmPassword.length > 0 && password === confirmPassword;

        return { min, upper, special, match };
    }, [password, confirmPassword]);

    const passwordRulesMain = [
        { key: "min", label: "8+ total characters", ok: passwordChecks.min },
        { key: "upper", label: "1+ uppercase letter", ok: passwordChecks.upper },
        { key: "special", label: "1+ special character", ok: passwordChecks.special },
    ];

    const passwordRuleMatch = { key: "match", label: "Passwords match", ok: passwordChecks.match };

    return (
        <div className="register-panel">
            <form
                method="post"
                className="register-form"
                onSubmit={(event) => {
                    if (validateRegisterForm(event)) {
                        adminReg ? HandleAdminRegister(event) : HandleRegister(event);
                    }
                }}
            >
                {!firstWait && (
                    <div className="center-row">
                        <div className="spinner-black" />
                    </div>
                )}

                {firstWait && (
                    <>
                        {lockedEmail && (
                            <>
                                <div className="field locked-email-field">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        name="email"
                                        aria-label="Email address"
                                        value={email}
                                        readOnly
                                        className="locked-email-input"
                                    />
                                </div>

                                {providerManaged && (
                                    <div className="managed-by-row">
                                        <span className="managed-by-text">
                                            Managed by {googleManaged ? "Google" : "Microsoft"}
                                        </span>
                                        <img
                                            src={googleManaged ? "/static/google-logo.png" : "/static/microsoft-logo.png"}
                                            alt={googleManaged ? "Google" : "Microsoft"}
                                            className="managed-by-logo"
                                        />
                                    </div>
                                )}


                                {/* Code goes directly under email (gate early, before other fields) */}
                                {registerWithCode && (
                                    <div className="field code-field">
                                        <label className="code-label">Code</label>
                                        <input
                                            type="text"
                                            name="class_code"
                                            aria-label="Class code"
                                            placeholder="Enter class code"
                                            className="code-input"
                                            required
                                        />
                                    </div>
                                )}
                            </>
                        )}

                        {!lockedEmail && (
                            <div className="field">
                                <label>Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    aria-label="Email address"
                                    value={email}
                                    onChange={(event) => setEmail(event.target.value)}
                                    placeholder="you@school.edu"
                                    required
                                />
                            </div>
                        )}

                        <div className="register-divider" />

                        <div className="two-col">
                            <div className="two-col-field">
                                <label>First Name</label>
                                <input
                                    type="text"
                                    name="first_name"
                                    aria-label="First name"
                                    defaultValue={firstName}
                                    placeholder="First Name"
                                    required
                                />
                            </div>

                            <div className="two-col-field">
                                <label>Last Name</label>
                                <input
                                    type="text"
                                    name="last_name"
                                    aria-label="Last name"
                                    defaultValue={lastName}
                                    placeholder="Last Name"
                                    required
                                />
                            </div>
                        </div>

                        <div className="field school-field">
                            <label>School</label>
                            <AutocompleteCollege
                                claimedEmail={email}
                                setLocation={(newloc) => setReportedCollege(newloc)}
                                detectedSubdomain={subdomain}
                                setAllValidColleges={setValidColleges}
                                hidden={false}
                            />
                        </div>

                        {/* Always submit college */}
                        <input type="hidden" name="college" value={reportedCollege} />

                        {/* Password */}
                        {!providerManaged && (
                            <>
                                <div className="field">
                                    <label>Password</label>

                                    <div className="password-wrap">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            aria-label="Password"
                                            placeholder="Password"
                                            required
                                            minLength={8}
                                            maxLength={40}
                                            value={password}
                                            onChange={(e) => setPassword(e.target.value)}
                                        />

                                        <button
                                            type="button"
                                            className="password-eye"
                                            aria-label={showPassword ? "Hide password" : "Show password"}
                                            onClick={() => setShowPassword((v) => !v)}
                                        >
                                            <img
                                                src={
                                                    showPassword
                                                        ? "/static/icons/eye-closed.png"
                                                        : "/static/icons/eye-open.png"
                                                }
                                                alt=""
                                                className="password-eye-icon"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <div className="password-rules">
                                    {passwordRulesMain.map((rule) => (
                                        <div
                                            key={rule.key}
                                            className={`password-rule ${rule.ok ? "ok" : ""}`}
                                        >
                                            <span className="rule-icon" aria-hidden="true">
                                                {rule.ok ? "✓" : "•"}
                                            </span>
                                            <span>{rule.label}</span>
                                        </div>
                                    ))}
                                </div>

                                <div className="field">
                                    <label>Confirm Password</label>

                                    <div className="password-wrap">
                                        <input
                                            type={showConfirmPassword ? "text" : "password"}
                                            name="confirm_password"
                                            aria-label="Confirm password"
                                            placeholder="Confirm Password"
                                            required
                                            minLength={8}
                                            maxLength={40}
                                            value={confirmPassword}
                                            onChange={(e) => setConfirmPassword(e.target.value)}
                                        />

                                        <button
                                            type="button"
                                            className="password-eye"
                                            aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                                            onClick={() => setShowConfirmPassword((v) => !v)}
                                        >
                                            <img
                                                src={
                                                    showConfirmPassword
                                                        ? "/static/icons/eye-closed.png"
                                                        : "/static/icons/eye-open.png"
                                                }
                                                alt=""
                                                className="password-eye-icon"
                                            />
                                        </button>
                                    </div>
                                </div>

                                <div className="password-rules password-rules-match">
                                    <div className={`password-rule ${passwordRuleMatch.ok ? "ok" : ""}`}>
                                        <span className="rule-icon" aria-hidden="true">
                                            {passwordRuleMatch.ok ? "✓" : "•"}
                                        </span>
                                        <span>{passwordRuleMatch.label}</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {providerManaged && (
                            <>
                                <input
                                    type="hidden"
                                    name="password"
                                    value={googleManaged ? "google-auth" : "microsoft-auth"}
                                />
                                <input
                                    type="hidden"
                                    name="confirm_password"
                                    value={googleManaged ? "google-auth" : "microsoft-auth"}
                                />
                            </>
                        )}


                        {error_message.length >= 1 && (
                            <div className="error-box">{renderMessageWithLink(error_message, error_link)}</div>
                        )}

                        <button
                            className="primary-btn"
                            type="submit"
                            disabled={waiting}
                        >
                            {waiting ? (
                                <span className="btn-inline">
                                    <span className="spinner" />
                                </span>
                            ) : (
                                "Register"
                            )}
                        </button>
                    </>
                )}
            </form>

            {theme.logo && !theme.logo.includes("rezify") && !adminReg && (
                <>
                    <div className="register-divider" />

                    <div className="register-options">
                        <a
                            href="https://rezify.ai/register"
                            className="register-link"
                        >
                            Leave School Portal
                        </a>
                    </div>
                </>
            )}
        </div>
    );
}

export { RegisterPanel };
